import React, { useEffect, useRef, useState } from 'react';
import Slider from 'react-rangeslider';
import 'react-rangeslider/lib/index.css';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import Loading from '../Shared/Loader';
import URLs from '../../utils/url'; // change later
import './index.scss';
import If from '../If';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { getBestSellerProduct } from '../../redux/actions/customizer';
import VideoWithFallback from '../VideoWithFallback';
import { getFontOptions, getSelectedOption } from '../../utils/commonRingUtils';
import EngravingFilter from '../EngravingFilter';
import Accordion from '../Accordion';

type IProps = {
  id: string;
  handleBestSeller: (id: string) => void;
  engraving: string;
  setEngraving: (text: string) => void;
  handleBestSellerDone: () => void;
  product: any;
  navigateToUrl: (url: string, isNewTab?: boolean) => void;
  onClose: () => void;
};

let timedOutId: NodeJS.Timeout | undefined;

const DiscountedPopup = ({
  id,
  handleBestSeller,
  product,
  navigateToUrl,
  engraving,
  setEngraving,
  handleBestSellerDone,
  onClose
}: IProps) => {
  const { bestSellerProduct, isFetching } = useAppSelector((state) => state.customizer);
  const dispatch = useAppDispatch();
  const [sliderValue, setSliderValue] = useState(10);

  useEffect(() => {
    if (!bestSellerProduct || bestSellerProduct?.bestseller_id !== id) {
      fetchBestSellerProduct();
    }
  }, [id]);

  // useEffect(() => {
  //   if (product && bestSellerProduct && product?.ring_options_url !== bestSellerProduct?.ring_options_url) {
  //     navigateToUrl(bestSellerProduct?.ring_options_url, true);
  //   }
  // }, [product, bestSellerProduct]);

  const fetchBestSellerProduct = async (data?: { size: number; engraving: string }) => {
    const { size, engraving } = data || {};
    try {
      const payload = {
        id,
        data: {
          size: size || '',
          engraving: engraving || ''
        }
      };
      await dispatch(getBestSellerProduct(payload)).unwrap();
    } catch (error) {
      console.log(error);
    }
  };

  const handleClick = (id: string) => () => {
    handleBestSeller(id);
  };

  const handleOnChange = (updated: any) => {
    if (updated.engraving_new?.includes('None')) {
      setEngraving('');
      fetchBestSellerProduct({ size: sliderValue, engraving: '' });
    } else {
      setEngraving(updated.engraving_new);
      fetchBestSellerProduct({ size: sliderValue, engraving: updated.engraving_new });
    }
  };

  const getMinRange = (list: any[]) => (list && list.length > 0 ? parseFloat(list[0].sku) : 3);

  const getMaxRange = (list: any[]) => (list && list.length > 0 ? parseFloat(list[list.length - 1].sku) : 24);

  const getStep = (list: any[]) => (list && list.length > 0 ? parseFloat(list[1].sku) - parseFloat(list[0].sku) : 0.25);

  const handleSliderChange = (value: number) => {
    setSliderValue(value);
    if (timedOutId) {
      clearTimeout(timedOutId);
    }
    timedOutId = setTimeout(() => {
      fetchBestSellerProduct({ size: value, engraving: engraving });
    }, 1000);
  };

  const getEngravingContent = () => {
    const content = {
      hasSwatch: false,
      fieldType: 'engraving_new',
      options: getFontOptions(
        bestSellerProduct?.engraving?.fonts || bestSellerProduct?.engraving_new?.text?.fonts || []
      ),
      selectedOption: getSelectedOption(
        bestSellerProduct?.engraving?.fonts || bestSellerProduct?.engraving_new?.text?.fonts || []
      ),
      engravingText: bestSellerProduct?.engraving_new?.text?.text || ''
    };
    return (
      <div className="tw-w-full tw-h-full tw-bg-blue-50">
        <EngravingFilter onChange={handleOnChange} isFetching={false} content={content} />
      </div>
    );
  };

  const isEngravingEnabled = bestSellerProduct?.is_engraving_enabled;
  return (
    <div className="-tw-my-5 tw-p-5 xl:tw-p-8 tw-flex tw-flex-wrap tw-gap-4 xl:tw-gap-8 tw-min-h-[300px] tw-max-h-[90vh]">
      <If condition={!!bestSellerProduct}>
        <div className="tw-w-full lg:tw-flex-1 shadowBox">
          <div className="tw-px-5">
            <h4 className="tw-font-reckless tw-text-center tw-text-xl tw-font-bold">Ready to Ship</h4>
            <div className="tw-flex tw-justify-center tw-my-3">
              <img
                src={`${URLs.assetsBaseUrl}${bestSellerProduct?.images[0]}`}
                alt="ring"
                className="tw-h-52 tw-object-cover"
              />
            </div>
            <If condition={bestSellerProduct?.variants?.length > 1}>
              <div className="tw-flex tw-flex-col sm:tw-flex-row  tw-flex-wrap tw-justify-center tw-gap-3 tw-mb-8">
                {bestSellerProduct?.variants?.map((variant: any) => (
                  <div
                    key={variant.bestseller_id}
                    onClick={!variant.selected ? handleClick(variant.bestseller_id) : undefined}
                    className={clsx('tw-text-sm', {
                      'solid-border': !variant.selected,
                      'dotted-border': variant.selected
                    })}
                  >
                    {variant.name}
                  </div>
                ))}
              </div>
            </If>
            <div className="tw-text-primary-color">{bestSellerProduct?.description}</div>
            <div className="tw-my-5">
              <span className="tw-text-brand-color tw-line-through">{bestSellerProduct?.msrp}</span>
              <span className="tw-text-primary-color tw-pl-2">{bestSellerProduct?.price}</span>
              <span className="tw-text-primary-color tw-italic tw-pl-2">in stock discount</span>
            </div>
            <div className="tw-flex tw-flex-col tw-gap-2">
              <div className="tw-text-primary-color tw-text-base tw-font-bold">
                Estimate your Size : <b>{sliderValue}</b>
              </div>
              <Slider
                min={getMinRange(bestSellerProduct?.finger_size || bestSellerProduct?.size)}
                max={getMaxRange(bestSellerProduct?.finger_size || bestSellerProduct?.size)}
                step={getStep(bestSellerProduct?.finger_size || bestSellerProduct?.size)}
                value={sliderValue}
                tooltip={true}
                onChange={handleSliderChange}
              />
            </div>
            <If condition={(bestSellerProduct?.engraving?.fonts || bestSellerProduct?.engraving_new?.text?.fonts || []).length > 0}>
              <div className="tw-flex tw-flex-col tw-gap-2 -tw-mx-4">
                <If condition={Boolean(!isEngravingEnabled)}>
                  <Accordion
                    title="Choose Engraving :"
                    isJXSContent
                    content={getEngravingContent()}
                    height="max-content"
                  />
                </If>
              </div>
            </If>
          </div>
          <div className="tw-mt-5">
            <button
              className="hover:tw-bg-brand-color hover:tw-text-tertiary-color tw-block tw-bg-brand-color tw-font-medium tw-px-9 tw-py-2 tw-rounded-tertiary tw-text-tertiary-color tw-w-full tw-text-center"
              onClick={handleBestSellerDone}
            >
              Proceed As Is
            </button>
          </div>
        </div>
        <div className="tw-w-full lg:tw-flex-1 shadowBox">
          <h4 className="tw-font-reckless tw-text-center tw-text-xl tw-font-bold">Make it Your Own</h4>
          <div className="tw-flex-1 tw-my-3">
            <VideoWithFallback videoUrl={bestSellerProduct?.video_url} className="discounted-video-box" />
          </div>
          <div className="tw-mt-5">
            <button
              // to={`/shop${bestSellerProduct?.ring_options_url}`}
              // target="_blank"
              onClick={onClose}
              className="hover:tw-bg-brand-color hover:tw-text-tertiary-color tw-block tw-bg-brand-color tw-font-medium tw-px-9 tw-py-2 tw-rounded-tertiary tw-text-tertiary-color tw-w-full tw-text-center"
            >
              Customize in Lashbrook Builder
            </button>
          </div>
        </div>
      </If>
      <Loading timedOut={isFetching} />
    </div>
  );
};

export default DiscountedPopup;
